import { API } from '@/models/api';
import { BaseResponse } from '@/models/baseResponse';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, shareReplay, switchMap } from 'rxjs';
import { AssistTablesService } from '@/services/assist.service';
import { ASSIST_TABLE_CODES } from '@/models/assist-table-codes';
import { getShipmentProfileDateColumns } from '../config/shipment-table-column-config';
import { parseDate } from 'src/app/core/utils/utils';

@Injectable({ providedIn: 'root' })
export class LogisticsService {
  private http = inject(HttpClient);
  private assistSrv = inject(AssistTablesService);
  private shipmentCollectionsCache = new Map<string, Observable<any>>();
  private shipmentProfileDateColumns = getShipmentProfileDateColumns();
  private locationData$: Observable<{
    cityNames: Map<string, string>;
    countryNames: Map<string, string>;
  }> | null = null;

  getLocationData() {
    if (!this.locationData$) {
      this.locationData$ = this.assistSrv
        .getAssistValuesByTableCode(ASSIST_TABLE_CODES.LOCATION)
        .pipe(
        map((locations) => {
          const cityNames = new Map<string, string>();
          const countryNames = new Map<string, string>(); // keyed by 2-char country code
          for (const location of locations ?? []) {
            const key = String(location?.key ?? '')
              .trim()
              .toUpperCase();
            const value = String(location?.value ?? '').trim();
            if (!key) continue;
            // value format: "FRCDG - FRCDG CHARLES-DE-GAULLE APT/PARIS, France"
            const afterKey = value.toUpperCase().startsWith(key) ? value.slice(key.length) : value;
            const withoutSeparator = afterKey.replace(/^\s*-?\s*/, '');
            const parts = withoutSeparator.split(',');
            const cityName = parts[0]
              .replace(/^[A-Z]{5}\s+/, '') // strip embedded code if S3 name started with it
              .replace(/\s*\([A-Z]{3}\)\s*$/i, '') // strip trailing "(CDG)" style suffix
              .trim();
            const countryName = parts[1]?.trim() ?? '';
            const countryCode = key.slice(0, 2);
            if (cityName) cityNames.set(key, cityName);
            if (countryName && countryCode) countryNames.set(countryCode, countryName);
          }
          return { cityNames, countryNames };
        }),
        shareReplay(1),
      );
    }
    return this.locationData$;
  }

  getAllShipments(filterGroups: any[][]): Observable<any[]> {
    const requestId = Math.floor(100 + Math.random() * 900);

    const totalStart = performance.now();

    const body = (filterGroups ?? [])
      .map((g) => (g ?? []).filter((f) => f?.fieldName && f?.matchMode))
      .filter((g) => g.length > 0);

    const httpStart = performance.now();

    return forkJoin([
      this.http.patch<any[]>(API.Shipment.All.url, body),
      this.getLocationData(),
    ]).pipe(
      map(([items, { cityNames: locationNameByCode, countryNames: countryNameByCode }]) => {
        const httpEnd = performance.now();
        const processingStart = performance.now();

        const dateColumns = this.shipmentProfileDateColumns;

        const mapped = (items ?? []).map((x: any) => {
          const parsedDates = dateColumns.reduce(
            (acc, field) => ({
              ...acc,
              [field]: parseDate(x?.[field]),
            }),
            {} as Record<string, Date | null>,
          );

          const origin = this.resolveLocationDisplay(
            x?.origin_CountryCode,
            x?.origin_LocationCode,
            locationNameByCode,
            countryNameByCode,
          );
          const destination = this.resolveLocationDisplay(
            x?.destination_CountryCode,
            x?.destination_LocationCode,
            locationNameByCode,
            countryNameByCode,
          );

          return {
            ...x,
            ...parsedDates,
            fullOrigin: origin.fullCode,
            fullDestination: destination.fullCode,
            originShortStation: origin.shortCode,
            destinationShortStation: destination.shortCode,
            originStationName: origin.stationName,
            destinationStationName: destination.stationName,
            originStationNameDisplay: origin.nameWithCode,
            destinationStationNameDisplay: destination.nameWithCode,
            originCountryNameDisplay: origin.countryName
              ? `${origin.countryName}(${origin.countryCode})`
              : origin.countryCode,
            destinationCountryNameDisplay: destination.countryName
              ? `${destination.countryName}(${destination.countryCode})`
              : destination.countryCode,
            hblMblSort: x?.housebillNumber ?? '',
            originDestination: `${x?.origin ?? ''} ${x?.destination ?? ''}`.trim(),
            shipperConsignee: `${x?.shipperName ?? ''} ${x?.consigneeName ?? ''}`.trim(),
            departureSortDate: x?.atd ?? x?.etd ?? null,
            arrivalSortDate: x?.ata ?? x?.eta ?? null,
          };
        });

        const totalEnd = performance.now();
        console.log(
          `[${requestId}] getAllShipments TOTAL time (loading + mapping): ${mapped.length} results in ${(totalEnd - totalStart).toFixed(2)} ms`,
        );

        return mapped;
      }),
    );
  }

  getDocument(id: string): Observable<any> {
    return this.http.get(API.Shipment.GetDocument(id).url, {
      responseType: 'blob' as const,
      headers: { Accept: 'application/pdf' },
      withCredentials: true,
      observe: 'response',
    });
  }

  getShipmentCollectionsById(shipmentId: string): Observable<any> {
    if (!this.shipmentCollectionsCache.has(shipmentId)) {
      const request$ = this.assistSrv
        .getAssistValuesByTableCodes([
          ASSIST_TABLE_CODES.LOCATION,
          ASSIST_TABLE_CODES.COUNTRY,
          ASSIST_TABLE_CODES.EVENT,
        ])
        .pipe(
          switchMap((assistcodes) => {
            const locations = [
              ...(assistcodes[ASSIST_TABLE_CODES.LOCATION] ?? []),
              ...(assistcodes[ASSIST_TABLE_CODES.COUNTRY] ?? []),
            ];
            const events2 = assistcodes[ASSIST_TABLE_CODES.EVENT] ?? [];

            return this.http
              .get<BaseResponse>(API.Shipment.GetShipmentCollectionsById(shipmentId).url)
              .pipe(
                map((res: any) => {
                  if (!res.success) return null;

                  const data = res.data;
                  const mapped = {
                    ...data,
                    statuses: data.statuses
                      .sort((a: any, b: any) => {
                        if (a.statusDateOriginal === null) return 1;
                        if (b.statusDateOriginal === null) return -1;
                        return (
                          new Date(b.statusDateOriginal).getTime() -
                          new Date(a.statusDateOriginal).getTime()
                        );
                      })
                      .map?.((s: any) => ({
                        ...s,
                        eventlocation: this.getLocationDesc(s.eventlocation, locations),
                        name: events2?.find((ev: any) => ev.key == s.statusesCode)?.value,
                        description: events2?.find((ev: any) => ev.key == s.statusesCode)
                          ?.description,
                      })),
                  };
                  return mapped;
                }),
              );
          }),
          shareReplay(1),
        );
      this.shipmentCollectionsCache.set(shipmentId, request$);
    }
    return this.shipmentCollectionsCache.get(shipmentId)!;
  }

  getLocationDesc(loc: string, locations: any[]) {
    // return loc
    return locations?.find?.((l: any) => l?.key == loc)?.description ?? loc;
  }

  // Builds the normalized 5-char location code plus the short-code/name display variants
  // needed by the dedicated Origin/Destination columns; all values are used as the
  // filterable/sortable field values too, so they must match what's shown on screen exactly.
  private resolveLocationDisplay(
    countryCode: string | null | undefined,
    locationCode: string | null | undefined,
    locationNameByCode: Map<string, string>,
    countryNameByCode: Map<string, string>,
  ) {
    const normalizedCountryCode = String(countryCode ?? '')
      .trim()
      .toUpperCase();
    const normalizedLocationCode = String(locationCode ?? '')
      .trim()
      .toUpperCase();
    const locationCodeWithoutSeparator = normalizedLocationCode.replace(/[^A-Z]/g, '');
    const embeddedFullCode = locationCodeWithoutSeparator.match(/^[A-Z]{2}[A-Z]{3}/)?.[0] ?? '';
    const resolvedCountryCode = normalizedCountryCode || embeddedFullCode.slice(0, 2);
    const countryName = countryNameByCode.get(resolvedCountryCode) ?? '';
    const locationCodePart =
      embeddedFullCode && embeddedFullCode.startsWith(resolvedCountryCode)
        ? embeddedFullCode.slice(2)
        : locationCodeWithoutSeparator.slice(-3);
    const fullLocationCode = `${resolvedCountryCode}${locationCodePart}`;
    const stationName = locationNameByCode.get(fullLocationCode) || '';
    const shortCode = fullLocationCode.slice(-3);

    return {
      fullCode: fullLocationCode,
      shortCode,
      stationName,
      nameWithCode: stationName ? `${stationName}(${shortCode})` : shortCode,
      countryCode: resolvedCountryCode,
      countryName,
    };
  }
}
